import { Box } from '@chakra-ui/react';
import * as d3 from 'd3';
import React, { useEffect, useRef } from 'react';

interface WorldMapProps {
  events: Array<{
    EventoId?: number;
    Titolo: string;
    DataEvento: string;
  }>;
  userPastEventIds?: number[];
  userFutureEventIds?: number[];
  isUserLoggedIn?: boolean;
}

const WorldMap: React.FC<WorldMapProps> = ({ events, userPastEventIds = [], userFutureEventIds = [], isUserLoggedIn = false }) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!svgRef.current || !containerRef.current) return;

    // Get container width for responsive sizing
    const containerWidth = containerRef.current.offsetWidth;
    const width = containerWidth;
    const height = width * 0.5; // 2:1 aspect ratio

    // Clear any existing content
    d3.select(svgRef.current).selectAll("*").remove();

    const svg = d3.select(svgRef.current)
      .attr("width", width)
      .attr("height", height)
      .style("background", "transparent")
      .style("overflow", "hidden");

    const projection = d3.geoNaturalEarth1()
      .scale(width / 5.5)
      .translate([width / 2, height / 2]);

    const path = d3.geoPath().projection(projection);

    // Load and render the world map
    d3.json("/countries.geo.json").then((data: any) => {
      // Draw countries
      svg.selectAll("path")
        .data(data.features)
        .enter()
        .append("path")
        .attr("d", path as any)
        .attr("id", (d: any) => d.id)
        .attr("fill", "none")
        .attr("stroke", "rgba(255, 255, 255, 0.5)")
        .attr("stroke-width", 0.8);

      // Map country names to ISO codes
      const countryMapping: { [key: string]: string } = {
        'thailandia': 'THA',
        'thailand': 'THA',
        'turchia': 'TUR',
        'turkey': 'TUR',
        'cuba': 'CUB',
        'messico': 'MEX',
        'mexico': 'MEX',
        'marocco': 'MAR',
        'morocco': 'MAR',
        'mozambico': 'MOZ',
        'mozambique': 'MOZ',
        'india': 'IND',
        'kerala': 'IND',
        'portogallo': 'PRT',
        'portugal': 'PRT',
        'giappone': 'JPN',
        'japan': 'JPN',
        'cina': 'CHN',
        'china': 'CHN',
        'italia': 'ITA',
        'italy': 'ITA',
        'francia': 'FRA',
        'france': 'FRA',
        'spagna': 'ESP',
        'spain': 'ESP',
        'grecia': 'GRC',
        'greece': 'GRC',
        'etiopia': 'ETH',
        'ethiopia': 'ETH',
        'peru': 'PER',
        'perù': 'PER',
        'brasil': 'BRA',
        'brazil': 'BRA',
        'argentina': 'ARG',
        'vietnam': 'VNM',
        'corea': 'KOR',
        'korea': 'KOR',
        'south korea': 'KOR',
        'north korea': 'PRK',
        'libano': 'LBN',
        'lebanon': 'LBN',
        'indonesia': 'IDN',
        'australia': 'AUS',
        'usa': 'USA',
        'stati uniti': 'USA',
        'united states': 'USA',
        'germania': 'DEU',
        'germany': 'DEU',
        'inghilterra': 'GBR',
        'england': 'GBR',
        'gran bretagna': 'GBR',
        'regno unito': 'GBR',
        'uk': 'GBR',
        'irlanda': 'IRL',
        'ireland': 'IRL',
        'svezia': 'SWE',
        'sweden': 'SWE',
        'norvegia': 'NOR',
        'norway': 'NOR',
        'danimarca': 'DNK',
        'denmark': 'DNK',
        'finlandia': 'FIN',
        'finland': 'FIN',
        'islanda': 'ISL',
        'iceland': 'ISL',
        'russia': 'RUS',
        'filippine': 'PHL',
        'philippines': 'PHL',
        'mar baltico': 'FIN', // Finlandia
        'baltico': 'FIN',
        'lituania': 'LTU',
        'lithuania': 'LTU',
        'lettonia': 'LVA',
        'latvia': 'LVA',
        'estonia': 'EST',
        'polonia': 'POL',
        'poland': 'POL',
        'ucraina': 'UKR',
        'ukraine': 'UKR',
        'romania': 'ROU',
        'bulgaria': 'BGR',
        'ungheria': 'HUN',
        'hungary': 'HUN',
        'austria': 'AUT',
        'svizzera': 'CHE',
        'switzerland': 'CHE',
        'belgio': 'BEL',
        'belgium': 'BEL',
        'olanda': 'NLD',
        'paesi bassi': 'NLD',
        'netherlands': 'NLD',
        'lussemburgo': 'LUX',
        'luxembourg': 'LUX',
        'croazia': 'HRV',
        'croatia': 'HRV',
        'serbia': 'SRB',
        'slovenia': 'SVN',
        'bosnia': 'BIH',
        'albania': 'ALB',
        'montenegro': 'MNE',
        'macedonia': 'MKD',
        'egitto': 'EGY',
        'egypt': 'EGY',
        'tunisia': 'TUN',
        'algeria': 'DZA',
        'libia': 'LBY',
        'libya': 'LBY',
        'sudafrica': 'ZAF',
        'south africa': 'ZAF',
        'kenya': 'KEN',
        'tanzania': 'TZA',
        'uganda': 'UGA',
        'ghana': 'GHA',
        'nigeria': 'NGA',
        'senegal': 'SEN',
        'mali': 'MLI',
        'costa d\'avorio': 'CIV',
        'camerun': 'CMR',
        'zimbabwe': 'ZWE',
        'madagascar': 'MDG',
        'mauritius': 'MUS',
        'seychelles': 'SYC',
        'israele': 'ISR',
        'israel': 'ISR',
        'giordania': 'JOR',
        'jordan': 'JOR',
        'siria': 'SYR',
        'syria': 'SYR',
        'iraq': 'IRQ',
        'iran': 'IRN',
        'arabia saudita': 'SAU',
        'saudi arabia': 'SAU',
        'emirati arabi': 'ARE',
        'uae': 'ARE',
        'dubai': 'ARE',
        'qatar': 'QAT',
        'kuwait': 'KWT',
        'oman': 'OMN',
        'yemen': 'YEM',
        'pakistan': 'PAK',
        'afghanistan': 'AFG',
        'bangladesh': 'BGD',
        'sri lanka': 'LKA',
        'nepal': 'NPL',
        'bhutan': 'BTN',
        'myanmar': 'MMR',
        'birmania': 'MMR',
        'laos': 'LAO',
        'cambogia': 'KHM',
        'cambodia': 'KHM',
        'malaysia': 'MYS',
        'singapore': 'SGP',
        'singapour': 'SGP',
        'brunei': 'BRN',
        'taiwan': 'TWN',
        'hong kong': 'HKG',
        'macao': 'MAC',
        'mongolia': 'MNG',
        'kazakhstan': 'KAZ',
        'uzbekistan': 'UZB',
        'turkmenistan': 'TKM',
        'kirghizistan': 'KGZ',
        'tagikistan': 'TJK',
        'canada': 'CAN',
        'guatemala': 'GTM',
        'belize': 'BLZ',
        'honduras': 'HND',
        'el salvador': 'SLV',
        'nicaragua': 'NIC',
        'costa rica': 'CRI',
        'panama': 'PAN',
        'colombia': 'COL',
        'venezuela': 'VEN',
        'ecuador': 'ECU',
        'guyana': 'GUY',
        'suriname': 'SUR',
        'guyana francese': 'GUF',
        'bolivia': 'BOL',
        'cile': 'CHL',
        'chile': 'CHL',
        'paraguay': 'PRY',
        'uruguay': 'URY',
        'giamaica': 'JAM',
        'jamaica': 'JAM',
        'bahamas': 'BHS',
        'haiti': 'HTI',
        'repubblica dominicana': 'DOM',
        'porto rico': 'PRI',
        'trinidad': 'TTO',
        'barbados': 'BRB',
        'nuova zelanda': 'NZL',
        'new zealand': 'NZL',
        'papua nuova guinea': 'PNG',
        'fiji': 'FJI',
        'samoa': 'WSM',
        'tonga': 'TON',
        'vanuatu': 'VUT',
        'isole salomone': 'SLB'
      };

      // Get country code from event title
      const getCountryCode = (title: string): string | null => {
        const t = title.toLowerCase();
        for (const [key, code] of Object.entries(countryMapping)) {
          if (t.includes(key)) {
            return code;
          }
        }
        return null;
      };

      // Create pins for each event (past and future)
      const pinsGroup = svg.append("g").attr("class", "pins");

      events.forEach((evento, index) => {
        const countryCode = getCountryCode(evento.Titolo);
        if (!countryCode) return;

        // Find the country feature
        const countryFeature = data.features.find((f: any) => f.id === countryCode);
        if (!countryFeature) return;

        // Get centroid of the country
        const centroid = path.centroid(countryFeature as any);
        if (!centroid || isNaN(centroid[0]) || isNaN(centroid[1])) return;

        const [x, y] = centroid;
        
        // Determine pin appearance based on event type and user booking
        const isPastEvent = new Date(evento.DataEvento) < new Date();
        const userBookedPast = evento.EventoId && userPastEventIds.includes(evento.EventoId);
        const userBookedFuture = evento.EventoId && userFutureEventIds.includes(evento.EventoId);
        const isUserBooked = userBookedPast || userBookedFuture;
        
        let pinColor: string;
        let pinSize: number;
        let glowSize: number;
        
        if (isPastEvent) {
          if (userBookedPast) {
            // Past event with user booking: white star
            pinColor = "white";
            pinSize = 7;
            glowSize = 14;
          } else {
            // Past event without booking: white dot
            pinColor = "white";
            pinSize = 4;
            glowSize = 8;
          }
        } else {
          if (userBookedFuture) {
            // Future event with user booking: cyan star
            pinColor = "#00CED1";
            pinSize = 7;
            glowSize = 14;
          } else {
            // Future event without booking: light blue dot
            pinColor = "#87CEEB";
            pinSize = 4;
            glowSize = 8;
          }
        }

        // Create pin group
        const pinGroup = pinsGroup.append("g")
          .attr("class", "pin")
          .attr("transform", `translate(${x}, ${y})`);

        if (isUserBooked) {
          // For user booked events, add a star
          const starPath = "M 0,-8 L 2.4,-2.4 L 8.4,-1.6 L 4.2,2.8 L 5.2,8.8 L 0,5.6 L -5.2,8.8 L -4.2,2.8 L -8.4,-1.6 L -2.4,-2.4 Z";
          
          // Outer glow for star
          pinGroup.append("path")
            .attr("d", starPath)
            .attr("fill", pinColor)
            .attr("opacity", 0.3)
            .attr("filter", "blur(4px)")
            .attr("transform", "scale(1.5)");
          
          // Animated pulse star
          pinGroup.append("path")
            .attr("d", starPath)
            .attr("fill", pinColor)
            .attr("opacity", 0)
            .attr("class", `pulse-star-${index}`)
            .style("animation", `pulseStar 2s ease-out ${0.5 + index * 0.2}s infinite`);
          
          // Main star
          pinGroup.append("path")
            .attr("d", starPath)
            .attr("fill", pinColor)
            .attr("stroke", "rgba(0,0,0,0.3)")
            .attr("stroke-width", 0.5)
            .attr("opacity", 0)
            .transition()
            .delay(300 + index * 150)
            .duration(500)
            .attr("opacity", 1);
        } else {
          // For non-user events, use circles
          // Glow circle (outer glow)
          pinGroup.append("circle")
            .attr("r", glowSize)
            .attr("fill", pinColor)
            .attr("opacity", 0.2)
            .attr("filter", "blur(3px)");

          // Pulse circle (animated)
          pinGroup.append("circle")
            .attr("r", 0)
            .attr("fill", pinColor)
            .attr("opacity", 0.4)
            .attr("class", `pulse-${index}`)
            .style("animation", `pulse 2s ease-out ${0.5 + index * 0.2}s infinite`);

          // Main pin dot
          pinGroup.append("circle")
            .attr("r", 0)
            .attr("fill", pinColor)
            .attr("class", `pin-dot-${index}`)
            .transition()
            .delay(300 + index * 150)
            .duration(500)
            .attr("r", pinSize);
        }
      });

      // Add CSS animation for pulse
      const style = document.createElement('style');
      style.textContent = `
        @keyframes pulse {
          0% { r: 4; opacity: 0.6; }
          50% { r: 12; opacity: 0.15; }
          100% { r: 12; opacity: 0; }
        }
        @keyframes pulseStar {
          0% { transform: scale(1); opacity: 0.6; }
          50% { transform: scale(2.5); opacity: 0.15; }
          100% { transform: scale(2.5); opacity: 0; }
        }
      `;
      document.head.appendChild(style);
    });

    // Cleanup
    return () => {
      // Remove animation style on unmount
      const styles = document.querySelectorAll('style');
      styles.forEach(style => {
        if (style.textContent?.includes('@keyframes pulse')) {
          style.remove();
        }
      });
    };
  }, [events, userPastEventIds, userFutureEventIds]);

  return (
    <Box 
      w="100%" 
      maxW="1200px" 
      mx="auto"
      p={0}
      border="none"
      boxShadow="none"
      outline="none"
    >
      <Box 
        ref={containerRef} 
        w="100%" 
        overflow="hidden"
      >
        <svg ref={svgRef} style={{ width: '100%', height: 'auto', display: 'block', background: 'transparent', border: 'none', outline: 'none' }} />
      </Box>
      
      {/* Legend */}
      <Box 
        mt={4}
        color="white" 
        fontSize="sm"
        w="100%"
        maxW="800px"
        mx="auto"
      >
        <Box 
          display="grid" 
          gridTemplateColumns="1fr 1fr" 
          gap={4}
          justifyItems="center"
        >
          {isUserLoggedIn ? (
            <>
              {/* Past event - user attended */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <path 
                    d="M 10,3 L 11.5,7.5 L 16,8 L 13,11 L 13.5,15.5 L 10,13 L 6.5,15.5 L 7,11 L 4,8 L 8.5,7.5 Z" 
                    fill="white" 
                    stroke="rgba(255,255,255,0.3)" 
                    strokeWidth="0.5"
                  />
                </svg>
                <Box>Sei stato qui</Box>
              </Box>
              
              {/* Past event - not attended */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="4" fill="white" opacity="0.8" />
                </svg>
                <Box>Eventi passati</Box>
              </Box>
              
              {/* Future event - user booked */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <path 
                    d="M 10,3 L 11.5,7.5 L 16,8 L 13,11 L 13.5,15.5 L 10,13 L 6.5,15.5 L 7,11 L 4,8 L 8.5,7.5 Z" 
                    fill="#00CED1" 
                    stroke="rgba(0,206,209,0.3)" 
                    strokeWidth="0.5"
                  />
                </svg>
                <Box>Hai prenotato</Box>
              </Box>
              
              {/* Future event - not booked */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="4" fill="#87CEEB" opacity="0.8" />
                </svg>
                <Box>Eventi futuri</Box>
              </Box>
            </>
          ) : (
            <>
              {/* Simple legend for non-logged users */}
              {/* Past events */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="4" fill="white" opacity="0.8" />
                </svg>
                <Box>Eventi passati</Box>
              </Box>
              
              {/* Future events */}
              <Box display="flex" alignItems="center" gap={2}>
                <svg width="20" height="20" viewBox="0 0 20 20">
                  <circle cx="10" cy="10" r="4" fill="#87CEEB" opacity="0.8" />
                </svg>
                <Box>Eventi futuri</Box>
              </Box>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default WorldMap;
